import {
  DisabledReason,
  FormValueControl,
  ValidationError,
  WithOptionalFieldTree,
} from '@angular/forms/signals';
import {
  Component,
  computed,
  inject,
  input,
  model,
  OnDestroy,
  OnInit,
  output,
  TemplateRef,
} from '@angular/core';
import { FormFieldStateService } from '@ps-gui/shared-core';

@Component({ template: '' })
export abstract class AbstractBasicSignalInput<T>
  implements FormValueControl<T>, OnInit, OnDestroy
{
  private readonly formFieldStateService = inject(FormFieldStateService);

  readonly label = input<string>();
  readonly labelTemplate = input<TemplateRef<{ $implicit: string }>>();
  readonly labelPosition = input<'left' | 'right'>('left');

  readonly placeholder = input('');
  readonly value = model<T>('' as T);
  readonly currentFieldIndex = input<number | undefined>(undefined);
  readonly index = input<number | undefined>(undefined);

  clicked = output<void>();

  readonly isFocused = computed(
    () =>
      this.currentFieldIndex() === this.index() &&
      this.currentFieldIndex() != undefined &&
      this.index() != undefined,
  );
  //writeable state values
  readonly touched = model<boolean>(false);

  //read only state values
  readonly disabled = input<boolean>(false);
  readonly disabledReasons = input<readonly WithOptionalFieldTree<DisabledReason>[]>([]);
  readonly readonly = input<boolean>(false);
  readonly hidden = input<boolean>(false);
  readonly invalid = input<boolean>(false);
  readonly validationErrors = input<readonly WithOptionalFieldTree<ValidationError>[]>([]);

  //metadata values
  readonly name = input<string>('');

  //constraint values
  readonly required = input<boolean>(false);

  readonly showError = computed(
    () => this.invalid() && (this.touched() || this.fieldState()?.touched()),
  );

  onInput(event: Event) {
    const target = event.target as HTMLElement;

    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
      this.value.set((target as HTMLInputElement).value as T);
    } else if (target.tagName === 'DIV') {
      this.value.set(target.textContent as T);
    }
  }

  readonly fieldState = computed(() => this.formFieldStateService.getFormFieldState(this.name()));

  ngOnInit() {
    this.formFieldStateService.addFormFieldState(
      this.name(),
      this.disabled,
      this.hidden,
      this.touched,
      this.invalid,
    );
  }

  ngOnDestroy() {
    this.formFieldStateService.removeFormFieldState(this.name());
  }
}
